import { useCallback, useMemo, useState } from 'react';
import { FetchBaseQueryError } from '@reduxjs/toolkit/query';

import {
  useGetWebhooksQuery,
  useGetWebhookEventsQuery,
  useLazyGetWebhookEventQuery,
  useCreateWebhookMutation,
  useUpdateWebhookMutation,
  useDeleteWebhookMutation,
  useRetryWebhookEventMutation,
} from '../store/modules/webhooks/api';
import {
  WebhookEventType,
  WebhookEventDataType,
  WebhookEventPageDataType,
  UpdateWebhookObject,
  WebhookEventsSortQuery,
  SelectedFiltersType,
  WEBHOOK_EVENT_TYPES,
  WebhookEventName,
  RetryWebhookEventOptions,
  GetWebhookEventsQuery,
  WebhookRetryConflictError,
} from '../pages/MerchantWebhooks/types/types';

const isRetryConflictError = (error: unknown): boolean =>
  typeof error === 'object' &&
  error !== null &&
  'status' in error &&
  (error as FetchBaseQueryError).status === 409;

type UseWebhooksServiceOptions = {
  merchantId: string;
  skipFetch?: boolean;
  skipEventsFetch?: boolean;
};

const DEFAULT_EVENTS_PAGE_SIZE = 10;

const filtersToQueryParams = (
  filters: SelectedFiltersType,
): Pick<
  GetWebhookEventsQuery,
  | 'initialSendTo'
  | 'initialSendFrom'
  | 'lastAttemptTo'
  | 'lastAttemptFrom'
  | 'statuses'
  | 'eventTypes'
> => {
  let initialSendTo: string | undefined;
  if (filters.selectedInitialSendDateTo) {
    const date = new Date(filters.selectedInitialSendDateTo);
    date.setUTCHours(23, 59, 59, 59);
    initialSendTo = date.toISOString();
  }

  let initialSendFrom: string | undefined;
  if (filters.selectedInitialSendDateFrom) {
    const date = new Date(filters.selectedInitialSendDateFrom);
    date.setUTCHours(0, 0, 0, 0);
    initialSendFrom = date.toISOString();
  }

  let lastAttemptTo: string | undefined;
  if (filters.selectedLastAttemptDateTo) {
    const date = new Date(filters.selectedLastAttemptDateTo);
    date.setUTCHours(23, 59, 59, 59);
    lastAttemptTo = date.toISOString();
  }

  let lastAttemptFrom: string | undefined;
  if (filters.selectedLastAttemptDateFrom) {
    const date = new Date(filters.selectedLastAttemptDateFrom);
    date.setUTCHours(0, 0, 0, 0);
    lastAttemptFrom = date.toISOString();
  }

  return {
    initialSendTo,
    initialSendFrom,
    lastAttemptTo,
    lastAttemptFrom,
    statuses: filters.selectedStatusFilters.size
      ? Array.from(filters.selectedStatusFilters)
      : undefined,
    eventTypes: filters.selectedEventFilters.size
      ? Array.from(filters.selectedEventFilters)
      : undefined,
  };
};

/**
 * Custom hook that provides a unified interface for webhook operations.
 */
export const useWebhooksService = ({
  merchantId,
  skipFetch = false,
  skipEventsFetch = false,
}: UseWebhooksServiceOptions) => {
  const [selectedFilters, setSelectedFilters] = useState<SelectedFiltersType>({
    selectedStatusFilters: new Set(),
    selectedEventFilters: new Set(),
    selectedInitialSendDateTo: null,
    selectedInitialSendDateFrom: null,
    selectedLastAttemptDateTo: null,
    selectedLastAttemptDateFrom: null,
  });
  const [sortQuery, setSortQuery] = useState<WebhookEventsSortQuery | undefined>(undefined);
  const [eventsPageNumber, setEventsPageNumber] = useState(0);
  const [eventsPageSize, setEventsPageSizeState] = useState(DEFAULT_EVENTS_PAGE_SIZE);

  const {
    data: webhooks,
    isLoading: webhooksLoading,
    refetch: refetchWebhooks,
  } = useGetWebhooksQuery(merchantId, {
    skip: skipFetch || !merchantId,
  });

  const [createWebhookMutation, { isLoading: isCreating }] = useCreateWebhookMutation();
  const [updateWebhookMutation, { isLoading: isUpdating }] = useUpdateWebhookMutation();
  const [deleteWebhookMutation, { isLoading: isDeleting }] = useDeleteWebhookMutation();
  const [retryEvent] = useRetryWebhookEventMutation();

  const eventsQueryParams = useMemo((): GetWebhookEventsQuery | undefined => {
    if (!merchantId) {
      return undefined;
    }

    return {
      merchantId,
      pageSize: eventsPageSize,
      pageNumber: eventsPageNumber,
      sortBy: sortQuery?.sortBy,
      sortField: sortQuery?.sortField,
      ...filtersToQueryParams(selectedFilters),
    };
  }, [merchantId, eventsPageSize, eventsPageNumber, sortQuery, selectedFilters]);

  const {
    data: eventsData,
    isLoading: eventsQueryLoading,
    isFetching: eventsQueryFetching,
    isError: eventsQueryError,
    fulfilledTimeStamp,
    refetch: refetchWebhookEventsQuery,
  } = useGetWebhookEventsQuery(eventsQueryParams!, {
    skip: skipEventsFetch || !merchantId || !eventsQueryParams,
  });

  const [triggerGetSingleEvent] = useLazyGetWebhookEventQuery();

  const [updatedEvents, setUpdatedEvents] = useState<Map<string, WebhookEventDataType>>(new Map());

  const eventTypes: WebhookEventType[] = WEBHOOK_EVENT_TYPES;
  const isLoading = webhooksLoading && !webhooks;
  const isMutating = isCreating || isUpdating || isDeleting;

  const events: WebhookEventDataType[] = (eventsData?.data ?? []).map(
    (event) => updatedEvents.get(event.eventId) ?? event,
  );

  const eventsPageData: WebhookEventPageDataType = {
    pageNumber: eventsPageNumber,
    pageSize: eventsPageSize,
    totalPages: eventsData?.pageData.totalPages ?? null,
    totalRecords: eventsData?.pageData.totalRecords ?? null,
    unseenRecords: eventsData?.pageData.unseenRecords ?? null,
  };

  const eventsLastTableLoadDate: Date | null = fulfilledTimeStamp
    ? new Date(fulfilledTimeStamp)
    : null;
  const isEventsLoading = eventsQueryLoading && !eventsData && !eventsQueryError;
  const isEventsDataLoading = eventsQueryFetching;

  const fetchWebhooks = useCallback(() => {
    refetchWebhooks();
  }, [refetchWebhooks]);

  const setEventsPageSize = useCallback((pageSize: number) => {
    setEventsPageSizeState(pageSize);
    setEventsPageNumber(0);
  }, []);

  const setEventsSortQuery = useCallback((nextSortQuery: WebhookEventsSortQuery | undefined) => {
    setSortQuery(nextSortQuery);
    setEventsPageNumber(0);
  }, []);

  const applyEventsFilters = useCallback((filters: SelectedFiltersType) => {
    setSelectedFilters(filters);
    setEventsPageNumber(0);
  }, []);

  const refetchWebhookEvents = useCallback(() => {
    void refetchWebhookEventsQuery();
  }, [refetchWebhookEventsQuery]);

  const createWebhook = useCallback(
    async (
      endpoint: string,
      failureNotificationEmails: string[],
      eventSubscriptions: WebhookEventName[],
      secret: string,
      description: string,
      includeChildMerchantEvents = false,
      customRequestHeaders?: { [key: string]: string },
    ) => {
      return createWebhookMutation({
        merchantId,
        endpoint,
        failureNotificationEmails,
        eventSubscriptions,
        secret,
        description,
        includeChildMerchantEvents,
        customRequestHeaders,
      }).unwrap();
    },
    [createWebhookMutation, merchantId],
  );

  const updateWebhook = useCallback(
    async (subscriptionId: string, updateObject: UpdateWebhookObject) => {
      return updateWebhookMutation({
        subscriptionId,
        merchantId,
        endpoint: updateObject.endpoint,
        failureNotificationEmails: updateObject.failureNotificationEmails,
        eventSubscriptions: updateObject.eventSubscriptions,
        description: updateObject.description,
        includeChildMerchantEvents: updateObject.includeChildMerchantEvents,
        customRequestHeaders: updateObject.customRequestHeaders,
        secret: updateObject.secret,
      }).unwrap();
    },
    [updateWebhookMutation, merchantId],
  );

  const deleteWebhook = useCallback(
    async (subscriptionId: string) => {
      return deleteWebhookMutation({ subscriptionId, merchantId }).unwrap();
    },
    [deleteWebhookMutation, merchantId],
  );

  const retryWebhookEvent = useCallback(
    async (eventId: string, eventMerchantId?: string, options: RetryWebhookEventOptions = {}) => {
      const targetMerchantId = eventMerchantId ?? merchantId;

      try {
        await retryEvent({
          eventId,
          merchantId: targetMerchantId,
          useCurrentSubscription: options.useCurrentSubscription ?? false,
        }).unwrap();
      } catch (error) {
        if (isRetryConflictError(error)) {
          throw new WebhookRetryConflictError();
        }
        // fail silently, re-fetch the event to get the updated status
      }

      const updatedEvent = await triggerGetSingleEvent({
        eventId,
        merchantId: targetMerchantId,
      }).unwrap();
      setUpdatedEvents((prev) => new Map(prev).set(eventId, updatedEvent));
      return updatedEvent;
    },
    [retryEvent, merchantId, triggerGetSingleEvent],
  );

  return {
    webhooks: webhooks ?? [],
    eventTypes,
    isLoading,
    isMutating,

    events,
    eventsPageData,
    eventsLastTableLoadDate,
    eventsSortQuery: sortQuery,
    eventsSelectedFilters: selectedFilters,
    isEventsLoading,
    isEventsDataLoading,

    fetchWebhooks,
    createWebhook,
    updateWebhook,
    deleteWebhook,
    setEventsPageNumber,
    setEventsPageSize,
    setEventsSortQuery,
    applyEventsFilters,
    refetchWebhookEvents,
    retryWebhookEvent,
  };
};

export default useWebhooksService;
